import React, { useContext, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  calculatePageHeightScale,
  calculatePageWidthScale,
} from "../../../../../hooks/utils";
import { dispatchEvent, scrollPage } from "../../../../../utis/helper";
import usePdfSearch from "../../../../../utis/usePdfSearch";
import { EditorInfoContext } from "../../../../../utis/userInfoContext";
import Footer from "../../../../Layout/Footer/Footer";
import ElementOrder from "../elementOrder/ElementOrder";
import TagEditorSection from "../TagEditorSection";
import PdfCanvas from "./PdfCanvas";
import PdfElementsList from "./PdfElementsList";

const PdfViewer = ({
  pdfFile,
  zoom,
  docFileId,
  docId,
  editortype,
  pageNum,
  docFiles,
  timerOnDoc,
  onLoadSuccess,
  showToolbar,
  setZoomIndex,
  statHook,
  tlb,
}) => {
  const { editEnabled, setRectangles, setPageDimensions, editElement } =
    useContext(EditorInfoContext);
  const { sendScrollInfo, trackPageChange, elemClick, setVideoData, actionCableVideoMontior } = statHook || {};
  const { searchInPdf } = usePdfSearch();
  const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
  const page = hashParams?.get("page");
  const containerRef = useRef(null);
  const canvasRefs = useRef([]);
  const prevZoomRef = useRef(zoom.value);
  const userInteractedRef = useRef(false);
  // Track if page change is programmatic (back button, jump, etc.) vs scroll-based
  const isProgrammaticScrollRef = useRef(false);
  // Suppress scroll-based page updates and opt-in checks during orientation change re-render
  const isOrientationChangingRef = useRef(false);
  const pageObjectsRef = useRef([]);
  const pdfShapeRef = useRef(null);
  const cyoaOrientationRecalcRef = useRef(null);
  const isCYOARef = useRef(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [renderedPages, setRenderedPages] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [jumpPageNum, setJumpPageNum] = useState(null);
  // Used to force a full PDF re-render after phone orientation change so that
  // the page scale is recalculated for the new viewport dimensions.
  const [orientationKey, setOrientationKey] = useState(0);
  
  useEffect(() => {
    const header = document.querySelector('.header-editor');
    if (!header) return;
    const update = () => setHeaderHeight(header.offsetHeight || 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, [showToolbar]);

  // Track PDF loading state: document not loaded OR pages still rendering.
  // For stats-viewer, only one page is rendered, so consider it loaded when that page renders.
  // During orientation change, suppress the loader so the user isn't blocked from scrolling
  // while pages re-render (they appear progressively instead of showing a spinner).
  const isPdfLoading = !isOrientationChangingRef.current && (
    numPages === null || 
    (editortype === "stats-viewer" ? renderedPages < 1 : renderedPages < numPages)
  );

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setRenderedPages(0);
    setRectangles(new Array(numPages).fill([]));
  };

  const onPageRenderSuccess = () => {
    setRenderedPages((prev) => prev + 1);
    setTimeout(() => {
      let input = document.getElementById("pageNumber");
      if (!input) {
        return;
      }
      // Only scroll to page on first render or URL hash
      // Don't scroll on every page render (prevents interference with natural scrolling)
      if (isFirstRender || page !== null) {
        // Determine which page to scroll to
        let targetPage;
        if (page !== null) {
          // URL hash has priority
          targetPage = page;
          input.value = page;
        } else if (isFirstRender && pageNum) {
          // First render: use pageNum prop from EditKdfPage (more reliable than input.value)
          targetPage = pageNum;
          input.value = pageNum;
        }

        // Only scroll if we have a valid page number
        if (targetPage && targetPage !== "") {
          // Mark as programmatic scroll so inputRef can also scroll the page element into view if needed
          isProgrammaticScrollRef.current = true;
          scrollPage(targetPage, "instant");
        }
      }
    }, 100);
    setIsFirstRender(false);
  };

  // Laserlink visitor mode: block page-number updates from non-user scroll events.
  // During initial load/zoom/layout shifts, the container can emit scroll events
  // and our onScroll handler would compute page 2 and write it into #pageNumber.
  // That triggers opt-in overlay without the user actually scrolling.
  //
  // We only allow onScroll to update #pageNumber AFTER a real user interaction.
  useEffect(() => {
    if (editortype !== "laserlink") return;

    userInteractedRef.current = false;
    const wrapper = containerRef.current;

    const mark = () => {
      userInteractedRef.current = true;
    };

    // Window-level wheel/keys cover mouse wheel + keyboard navigation.
    window.addEventListener("wheel", mark, { passive: true });
    window.addEventListener("keydown", mark);
    // Touch covers mobile scroll gestures.
    window.addEventListener("touchstart", mark, { passive: true });
    // Mouse down covers scrollbar drag in many browsers.
    if (wrapper) wrapper.addEventListener("mousedown", mark);

    return () => {
      window.removeEventListener("wheel", mark);
      window.removeEventListener("keydown", mark);
      window.removeEventListener("touchstart", mark);
      if (wrapper) wrapper.removeEventListener("mousedown", mark);
    };
  }, [editortype]);

  // CYOA portrait: lock document.body so the browser native scrollbar disappears
  // and no rubber-band scroll shows around the fixed-position page.
  // Restored on unmount or when switching out of CYOA/portrait.
  useEffect(() => {
    const isCYOA = editortype === "laserlink" && tlb?.cyoa_enabled === true;
    if (!isCYOA) return;

    const applyLock = () => {
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      const isMobile = window.innerWidth < 1024;
      const isMobPortrait = isMobile && !isLandscape;
      const wrapper = containerRef.current;

      // Always clear body lock first
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";

      if (isMobPortrait) {
        // Mobile portrait: lock body scroll only — no position:fixed needed since
        // page centering now uses position:absolute within the wrapper (not viewport-fixed)
        document.body.style.overflow = "hidden";
        if (wrapper) {
          wrapper.style.overflowY = "hidden";
          wrapper.style.overflowX = "hidden";
        }
      } else {
        // Mobile landscape + desktop: always allow scroll (for viewing page content)
        if (wrapper) {
          wrapper.style.overflowY = "scroll";
          wrapper.style.overflowX = "hidden";
          wrapper.style.webkitOverflowScrolling = "touch";
        }
      }
    };

    // Call immediately for body lock, then again after mount so containerRef is attached
    applyLock();
    const mountTimer = setTimeout(applyLock, 0);

    let orientationLockTimer = null;
    const scheduleApplyLock = (delay = 300) => {
      clearTimeout(orientationLockTimer);
      orientationLockTimer = setTimeout(applyLock, delay);
    };

    // Real devices fire orientationchange; Chrome DevTools fires resize instead.
    // matchMedia "change" fires reliably in both cases when portrait↔landscape flips.
    const orientationMQ = window.matchMedia("(orientation: landscape)");
    const onOrientationMQChange = () => scheduleApplyLock(300);
    orientationMQ.addEventListener("change", onOrientationMQChange);
    // Keep orientationchange as belt-and-suspenders for older browsers
    window.addEventListener("orientationchange", () => scheduleApplyLock(300));

    const clearOnUnload = () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
    window.addEventListener("beforeunload", clearOnUnload);
    window.addEventListener("pagehide", clearOnUnload);

    return () => {
      clearTimeout(mountTimer);
      clearTimeout(orientationLockTimer);
      clearOnUnload();
      orientationMQ.removeEventListener("change", onOrientationMQChange);
      const wrapper = containerRef.current;
      if (wrapper) {
        wrapper.style.overflowY = "";
        wrapper.style.overflowX = "";
        wrapper.style.webkitOverflowScrolling = "";
      }
      window.removeEventListener("beforeunload", clearOnUnload);
      window.removeEventListener("pagehide", clearOnUnload);
    };
  }, [editortype, tlb?.cyoa_enabled]);

  // CYOA orientation recalc: keep a fresh closure in a ref so the orientation
  // effect (mounted once, deps=[]) can call it without capturing stale values.
  // For CYOA we recalculate scale for each stored page object in-place instead
  // of remounting pages (which would start a new DB session via WebSocket init).
  useEffect(() => {
    // Keep isCYOARef in sync — read by the [] orientation effect to prevent remount
    isCYOARef.current = editortype === "laserlink" && tlb?.cyoa_enabled === true;
  }); // no deps — runs every render

  useEffect(() => {
    cyoaOrientationRecalcRef.current = () => {
      const isCYOAMode = editortype === "laserlink" && tlb?.cyoa_enabled === true;
      if (!isCYOAMode) return false; // tell caller to fall back to full remount
      // Wrap in try/catch: if pages haven't loaded yet during rapid rotation,
      // don't throw — CYOA never falls through to remount regardless.
      try {

      const isLandscapeOrientation = window.matchMedia("(orientation: landscape)").matches;
      const isMobileLandscape =
        window.innerWidth < 1024 && isLandscapeOrientation;

      const vw = window.visualViewport?.width || window.innerWidth;
      const vh = window.visualViewport?.height || window.innerHeight;

      const isMobilePortrait = !isMobileLandscape && window.innerWidth < 768;

      // Read safe-area insets (set as CSS custom properties so env() resolves to px)
      const rootStyle = getComputedStyle(document.documentElement);
      const safeLeft = parseFloat(rootStyle.getPropertyValue('--safe-area-left')) || 0;
      const safeRight = parseFloat(rootStyle.getPropertyValue('--safe-area-right')) || 0;

      pageObjectsRef.current.forEach((pageObj, index) => {
        if (!pageObj) return;
        const canvas = canvasRefs.current[index];
        if (!canvas) return;
        const viewport = pageObj.getViewport({ scale: 1 });

        let newScale;
        if (isMobileLandscape) {
          // Landscape: subtract safe-area insets (notch/home indicator) + 20px each side
          const effectiveWidth = vw - safeLeft - safeRight - 40;
          newScale = parseFloat((effectiveWidth / viewport.width).toFixed(2));
        } else if (isMobilePortrait) {
          // Portrait: fit within the safe area (excludes notch + home indicator)
          const safeTop    = parseFloat(rootStyle.getPropertyValue('--safe-area-top'))    || 0;
          const safeBottom = parseFloat(rootStyle.getPropertyValue('--safe-area-bottom')) || 0;
          const fitWidthScale  = (vw - 16) / viewport.width;
          const fitHeightScale = (vh - safeTop - safeBottom - 16) / viewport.height;
          newScale = parseFloat(Math.min(fitWidthScale, fitHeightScale).toFixed(2));
        } else {
          // Desktop / tablet: full height priority, capped by width to avoid overflow
          const heightScale = vh / viewport.height;
          const widthScale  = vw / viewport.width;
          newScale = parseFloat(Math.min(heightScale, widthScale).toFixed(2));
        }

        if (zoom?.index == "page-height" || zoom?.index === "page-width") {
          window.objInit.zoom_lvl = newScale;
          setZoomIndex({ index: zoom.index, value: newScale });
        }

        const scaledVp = pageObj.getViewport({ scale: newScale });
        canvas.width = scaledVp.width;
        canvas.height = scaledVp.height;
        canvas.style.width = `${scaledVp.width}px`;
        canvas.style.height = `${scaledVp.height}px`;
      });

      setPageDimensions(() => {
        const firstCanvas = canvasRefs.current[0];
        return firstCanvas
          ? { width: firstCanvas.width, height: firstCanvas.height }
          : { width: 0, height: 0 };
      });

      // Landscape: reset scroll to top immediately AND after iOS finishes its own layout.
      // Doing it once races with iOS scroll restoration; twice guarantees it wins.
      // Portrait uses position:fixed so scrollTop is irrelevant there.
      if (isMobileLandscape && containerRef.current) {
        containerRef.current.scrollTop = 0;
        setTimeout(() => {
          if (containerRef.current) containerRef.current.scrollTop = 0;
        }, 100);
      }

      // Clear guard after layout stabilises — no page remount, no new session
      setTimeout(() => {
        isOrientationChangingRef.current = false;
        window.__kdf_orientation_changing = false;
      }, 400);
      } catch (e) {
        // non-fatal: pages may not be loaded yet during rapid rotation
        // still return true below — CYOA never falls through to remount
      }

      return true; // CYOA always handled — never remount
    };
  }); // no deps — runs every render to keep closure fresh

  // Auto-adapt to orientation changes on mobile without requiring refresh.
  // Debounced: iOS Safari fires orientationchange once + resize multiple times per rotation.
  // We collapse them into a single re-render and only act when orientation actually flipped.
  useEffect(() => {
    let lastOrientation = window.matchMedia("(orientation: landscape)").matches
      ? "landscape"
      : "portrait";
    let debounceTimer = null;

    const handleOrientationChange = () => {
      // Only phones need full re-render on rotation (tablets/desktop unaffected)
      if (window.innerWidth >= 1024 && window.innerHeight >= 1024) return;

      // Set guard IMMEDIATELY on raw event, before debouncing.
      isOrientationChangingRef.current = true;
      window.__kdf_orientation_changing = true;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const newOrientation = window.matchMedia("(orientation: landscape)").matches
          ? "landscape"
          : "portrait";

        // False alarm — e.g. Safari hiding/showing address bar fires resize
        // but orientation is unchanged. Clear the guards we set eagerly.
        if (newOrientation === lastOrientation) {
          isOrientationChangingRef.current = false;
          window.__kdf_orientation_changing = false;
          return;
        }
        lastOrientation = newOrientation;

        isOrientationChangingRef.current = true;
        window.__kdf_orientation_changing = true;
        userInteractedRef.current = false;

        // CYOA: recalculate scale in-place — no page remount, no new DB session.
        // Falls back to full remount for non-CYOA viewers.
        if (cyoaOrientationRecalcRef.current?.()) {
          return;
        }

        // Belt-and-suspenders: if the recalc threw or ref wasn't set yet,
        // still never remount for CYOA (would start a new DB session).
        if (isCYOARef.current) {
          // Clear the guard since we're not doing a full re-render
          setTimeout(() => {
            isOrientationChangingRef.current = false;
            window.__kdf_orientation_changing = false;
          }, 500);
          return;
        }

        // Non-CYOA: full re-render via key change so scale recomputes for new viewport
        setRenderedPages(0);
        setIsFirstRender(true);
        setOrientationKey((prev) => prev + 1);
      }, 500); // 500ms: gives iOS time to finish its resize-storm before we act
    };

    const orientationMQ = window.matchMedia("(orientation: landscape)");
    window.addEventListener("orientationchange", handleOrientationChange);
    orientationMQ.addEventListener("change", handleOrientationChange);

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("orientationchange", handleOrientationChange);
      orientationMQ.removeEventListener("change", handleOrientationChange);
    };
  }, []);

  // iOS Safari ignores overscroll-behavior-y:none (WebKit bug #275947).
  // Prevent pull-to-refresh by blocking downward touchmove when already at scroll-top.
  useEffect(() => {
    if (editortype !== "laserlink") return;
    const wrapper = containerRef.current;
    if (!wrapper) return;

    let startY = 0;

    const onTouchStart = (e) => {
      startY = e.touches[0].pageY;
    };

    const onTouchMove = (e) => {
      // CYOA portrait: block all vertical scroll — page must fit in one screen.
      // Check DOM class to avoid stale closure (tlb prop doesn't change but this is safer).
      const isCYOA = wrapper.closest('.hidden-for-cyoa') !== null;
      const isPortrait = !window.matchMedia("(orientation: landscape)").matches;
      if (isCYOA && isPortrait) {
        e.preventDefault();
        return;
      }

      // Non-CYOA (delay scroll) and CYOA landscape: only block pull-to-refresh.
      const dy = e.touches[0].pageY - startY;
      if (dy > 0 && wrapper.scrollTop <= 0) {
        e.preventDefault();
      }
    };

    wrapper.addEventListener("touchstart", onTouchStart, { passive: true });
    wrapper.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      wrapper.removeEventListener("touchstart", onTouchStart);
      wrapper.removeEventListener("touchmove", onTouchMove);
    };
  }, [editortype]);

  // Slow down vertical wheel-scroll speed on the visitor-facing (laserlink) page.
  // Intercepting the wheel event and applying a sub-1 multiplier gives the React
  // opt-in blocking machinery more time to render the overlay before the user can
  // scroll multiple pages past the opt-in trigger in a single frame.
  // Adjust SCROLL_SPEED_FACTOR (0.0 – 1.0) to taste; 0.6 = 60 % of native speed.
  const SCROLL_SPEED_FACTOR = 0.6;
  useEffect(() => {
    if (editortype !== "laserlink") return;
    const wrapper = containerRef.current;
    if (!wrapper) return;

    // FIX: Don't attach wheel handler if scroll control (CYOA or Delay) is active.
    // When scroll control is active, CSS overflow:hidden should work naturally.
    // The wheel handler bypasses CSS by directly manipulating scrollTop.
    const isScrollControlActive =
      tlb?.disable_scroll &&
      (tlb?.disable_settings?.disable_on === 'cyoa' ||
       tlb?.disable_settings?.disable_on === 'seconds');

    if (isScrollControlActive) {
      // Let CSS overflow:hidden handle scroll blocking
      return;
    }

    const handleWheel = (e) => {
      e.preventDefault();
      wrapper.scrollTop += e.deltaY * SCROLL_SPEED_FACTOR;
    };

    // passive: false is required so we can call preventDefault()
    wrapper.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      wrapper.removeEventListener("wheel", handleWheel);
    };
  }, [editortype, tlb?.disable_scroll, tlb?.disable_settings?.disable_on]);

  const onScroll = () => {
    // Suppress page updates during orientation change re-render
    if (isOrientationChangingRef.current) {
      console.log(`[PDFVIEWER] onScroll BLOCKED — orientation change in progress`);
      return;
    }
    // In laserlink, ignore programmatic/initial scroll events until user interacts.
    if (editortype === "laserlink" && !userInteractedRef.current) return;

    // CRITICAL: In mobile landscape CYOA, disable scroll-based page navigation
    // Scrolling is for viewing current page content only, not navigating between pages
    // Page changes should only happen via swipe/buttons
    // CRITICAL: In mobile landscape CYOA, disable scroll-based page navigation
    // Scrolling is for viewing current page content only, not navigating between pages
    // Page changes should only happen via swipe/buttons
    const isCYOA = editortype === "laserlink" && tlb?.cyoa_enabled === true;

    if (isCYOA) {
      // All CYOA modes: page changes are hotspot-driven only, never scroll-driven.
      // Mobile landscape: scroll is still allowed for viewing content (return just skips page update).
      // Desktop: absolute-positioned pages mean scroll height is invalid for page calculation anyway.
      return;
    }

    const wrapper = containerRef.current;
    const pageHeight = wrapper.scrollHeight / numPages;
    const scrollPosition = wrapper.scrollTop + wrapper.clientHeight / 2;
    const newPage = Math.ceil(scrollPosition / pageHeight);
    const input = document.getElementById("pageNumber");
    console.log(`[PDFVIEWER] onScroll: scrollTop=${wrapper.scrollTop}, scrollHeight=${wrapper.scrollHeight}, pageHeight=${pageHeight.toFixed(1)}, computed newPage=${newPage}, current input=${input?.value}`);
    if (newPage > 0 && newPage <= numPages) {
      // CRITICAL FIX: Only update and dispatch if page actually changed
      // This prevents duplicate stats during orientation changes / layout shifts
      if (newPage != input.value) {
        console.log(`[PDFVIEWER] PAGE CHANGED: ${input.value} → ${newPage}`);
        input.value = newPage;
        dispatchEvent("change", "pageNumber");
        if (editortype == 'laserlink') {
          sendScrollInfo(newPage);
          statHook?.trackPageChange?.(newPage);
        }
      }
    }
  };

  const updateDimensions = (page, index) => {
    requestAnimationFrame(() => {
      const page = document.querySelector(
        `.react-pdf__Page.page[data-page-number="${index + 1}"]`
      );

      if (page) {
        const rect = page.getBoundingClientRect();
        setPageDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    });
  };
  const onPageLoadSuccess = (page, index) => {
    updateDimensions(page, index);
    // Store page object for CYOA orientation-change recalculation
    pageObjectsRef.current[index] = page;
    let canvas = canvasRefs.current[index];
      if (canvas && page) {
          // Single rAF is enough — CYOA uses visualViewport directly (no DOM width needed).
          // Non-CYOA path reads pageRect.width but one frame is sufficient for CSS to settle.
          requestAnimationFrame(() => {
                  const viewport = page.getViewport({ scale: 1 });

                  // Determine PDF orientation
                  const pdfWidth = viewport.width;
                  const pdfHeight = viewport.height;
                  const isLandscapePDF = pdfWidth > pdfHeight;
                  const isSquarePDF = Math.abs(pdfWidth - pdfHeight) < 10; // Within 10 units considered square

                  // Store PDF shape for orientation recalc (set once; all pages share the same shape)
                  if (index === 0 || !pdfShapeRef.current) {
                    pdfShapeRef.current = isSquarePDF ? 'square' : isLandscapePDF ? 'landscape' : 'portrait';
                  }

                  // Mobile landscape detection
                  const isLandscapeOrientation = window.matchMedia("(orientation: landscape)").matches;
                  const isMobileLandscape = window.innerWidth < 1024 && isLandscapeOrientation;

                  // Calculate scale based on rendering rules
                  let newScale;

                  // CYOA mode: scale rules per PDF type × device orientation
                  //  Portrait KDF : full height everywhere; mobile portrait prefers full width
                  //                 when the resulting height still fits the display area
                  //  Landscape KDF: full height on desktop; full width on mobile (portrait + landscape)
                  //  Square KDF   : full width on mobile (portrait + landscape); full height on desktop
                  const isCYOAMode = editortype === "laserlink" && tlb?.cyoa_enabled === true;
                  if (isCYOAMode) {
                      const vw = window.innerWidth;
                  const vh = window.innerHeight;

                      const isMobilePortrait = !isMobileLandscape && window.innerWidth < 768;

                      if (isMobileLandscape) {
                          // Landscape: fill width minus 16px (8px each side) — scroll vertically for full content
                          const fitWidthScale = (vw - 16) / viewport.width;
                          newScale = parseFloat(fitWidthScale.toFixed(2));
                      } else if (isMobilePortrait) {
                          // Portrait: fit within the safe area (excludes notch + home indicator)
                          const rootSt = getComputedStyle(document.documentElement);
                          const safeTop    = parseFloat(rootSt.getPropertyValue('--safe-area-top'))    || 0;
                          const safeBottom = parseFloat(rootSt.getPropertyValue('--safe-area-bottom')) || 0;
                          const fitWidthScale  = (vw - 16) / viewport.width;
                          const fitHeightScale = (vh - safeTop - safeBottom - 16) / viewport.height;
                          newScale = parseFloat(Math.min(fitWidthScale, fitHeightScale).toFixed(2));
                      } else {
                          // Desktop / tablet: full height priority, capped by width to avoid overflow
                          const heightScale = vh / viewport.height;
                          const widthScale  = vw / viewport.width;
                          newScale = parseFloat(Math.min(heightScale, widthScale).toFixed(2));
                      }

                      if (zoom?.index == "page-height" || zoom?.index === "page-width") {
                          if (!window.objInit.zoom_lvl) window.objInit.zoom_lvl = newScale;
                          setZoomIndex({ index: zoom.index, value: newScale });
                      }
                      const scaledVp = page.getViewport({ scale: newScale });
                      canvas.width = scaledVp.width;
                      canvas.height = scaledVp.height;
                      canvas.style.width = `${scaledVp.width}px`;
                      canvas.style.height = `${scaledVp.height}px`;
                      setPageDimensions({ width: canvas.width, height: canvas.height });
                      return; // Skip generic scale logic below
                  }

                  if (isMobileLandscape) {
                      // Mobile Landscape Rules:
                      // - Landscape/Square KDF → Full width (zoomed in for readability)
                      // - Portrait KDF → Full height (default)
                      if (isLandscapePDF || isSquarePDF) {
                          newScale = calculatePageWidthScale(page);
                          if (index === 0) {
                              console.log('[Mobile Landscape]', {
                                  pdfType: isSquarePDF ? 'Square' : 'Landscape',
                                  mode: 'FULL WIDTH',
                                  pdfSize: `${pdfWidth.toFixed(0)}x${pdfHeight.toFixed(0)}`,
                                  scale: newScale.toFixed(3)
                              });
                          }
                      } else {
                          // Portrait PDF in mobile landscape - use height
                          newScale = zoom?.index == "page-height"
                              ? calculatePageHeightScale(page)
                              : calculatePageWidthScale(page);
                          if (index === 0) {
                              console.log('[Mobile Landscape]', {
                                  pdfType: 'Portrait',
                                  mode: 'FULL HEIGHT (default)',
                                  pdfSize: `${pdfWidth.toFixed(0)}x${pdfHeight.toFixed(0)}`,
                                  scale: newScale.toFixed(3)
                              });
                          }
                      }
                  } else {
                      // Mobile Portrait Rules:
                      const isMobilePortrait = window.innerWidth < 768;

                      if (isMobilePortrait && isLandscapePDF) {
                          // Landscape KDF on mobile portrait → Full width
                          newScale = calculatePageWidthScale(page);
                          if (index === 0) {
                              console.log('[Mobile Portrait]', {
                                  pdfType: 'Landscape',
                                  mode: 'FULL WIDTH',
                                  scale: newScale.toFixed(3)
                              });
                          }
                      } else if (isMobilePortrait && isSquarePDF) {
                          // Square KDF on mobile portrait → Full width
                          newScale = calculatePageWidthScale(page);
                          if (index === 0) {
                              console.log('[Mobile Portrait]', {
                                  pdfType: 'Square',
                                  mode: 'FULL WIDTH',
                                  scale: newScale.toFixed(3)
                              });
                          }
                      } else {
                          // Desktop / Portrait KDF → Use existing zoom mode (page-height)
                          newScale = zoom?.index == "page-height"
                              ? calculatePageHeightScale(page)
                              : calculatePageWidthScale(page);
                      }
                  }

                  /* COMMENTED OUT - Will use for later enhancement
                  // CRITICAL: For landscape CYOA, increase scale to fill viewport end-to-end
                  // This prevents second page from peeking through at the bottom
                  const isLandscape = window.matchMedia("(orientation: landscape)").matches;
                  const isCYOA = tlb?.cyoa_enabled === true;
                  const isLaserlink = editortype === "laserlink";
                  const isMobile = window.innerWidth < 768;

                  if (isLandscape && isCYOA && isLaserlink && isMobile && zoom?.index == "page-height") {
                      // Landscape CYOA: Use 100% of viewport height (full height)
                      // Combined with CSS that removes page gaps (border/margin),
                      // this ensures one FULL page is visible with NO second page peeking through
                      const { width: availableWidth, height: availableHeight } =
                          window.visualViewport
                              ? { width: window.visualViewport.width, height: window.visualViewport.height }
                              : { width: window.innerWidth, height: window.innerHeight };

                      const heightMultiplier = 1.0; // 100% - full viewport height
                      const heightScale = (availableHeight * heightMultiplier) / viewport.height;
                      newScale = parseFloat(heightScale.toFixed(2));
                  }
                  */

                  if (zoom?.index == "page-height" || zoom?.index === "page-width") {
                      if (!window.objInit.zoom_lvl) window.objInit.zoom_lvl = newScale;
                      setZoomIndex({ index: zoom.index, value: newScale });
                  }

                  const scaledViewport = page.getViewport({ scale: newScale });

                  // On mobile, detect actual Page width after CSS is applied and adjust scale if needed
                  // This ensures PDF uses full available width while maintaining element positioning
                  // IMPORTANT: Constrain to available viewport width to prevent horizontal overflow
                  if (window.innerWidth < 768) {
                      /* COMMENTED OUT - Will use for later enhancement
                      // CRITICAL FIX: Skip mobile width expansion for CYOA in landscape mode
                      // Problem: In landscape, viewport height is limited (e.g., 390px)
                      // If we expand width to fill viewport, the page height overflows → cropping from bottom
                      // Solution: Keep the original height-fitted scale, page will be narrow but fully visible
                      // This maintains "one page at a time" principle without needing scroll
                      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
                      const isCYOA = tlb?.cyoa_enabled === true;
                      const isLaserlink = editortype === "laserlink";

                      if (isLandscape && isCYOA && isLaserlink) {
                          // Landscape CYOA: Use height-fitted scale, don't expand to full width
                          // Trade-off: Page is narrow (~34% width) but fully visible vertically
                          // This matches portrait behavior: one full page visible, no scroll needed
                          canvas.width = scaledViewport.width;
                          canvas.height = scaledViewport.height;
                          canvas.style.width = `${scaledViewport.width}px`;
                          canvas.style.height = `${scaledViewport.height}px`;

                          setPageDimensions({
                              width: canvas.width,
                              height: canvas.height,
                          });
                          return; // Exit early, skip width expansion logic below
                      }
                      */

                      // Normal mobile handling: Portrait mode OR non-CYOA (and now ALL landscape)
                      const pageElement = document.querySelector(
                          `.react-pdf__Page.page[data-page-number="${index + 1}"]`
                      );
                      if (pageElement) {
                          const pageRect = pageElement.getBoundingClientRect();
                          // Get available viewport width to ensure we don't exceed it
                          const availableWidth = window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth;

                          // If CSS has expanded the Page beyond calculated width, recalculate scale
                          // But constrain it to available viewport width to prevent overflow
                          if (pageRect.width > scaledViewport.width) {
                              // Use the smaller of: pageRect.width (CSS expanded) or availableWidth (viewport constraint)
                              const constrainedWidth = Math.min(pageRect.width, availableWidth);
                              const actualScale = constrainedWidth / viewport.width;
                              const recalculatedViewport = page.getViewport({ scale: actualScale });

                              // Update zoom state so react-pdf re-renders with correct scale
                              if (zoom?.index == "page-height" || zoom?.index === "page-width") {
                                  setZoomIndex({ index: zoom.index, value: actualScale });
                                  if (!window.objInit.zoom_lvl) window.objInit.zoom_lvl = actualScale;
                              }

                              // Update overlay canvas dimensions
                              canvas.width = recalculatedViewport.width;
                              canvas.height = recalculatedViewport.height;
                              canvas.style.width = `${recalculatedViewport.width}px`;
                              canvas.style.height = `${recalculatedViewport.height}px`;

                              setPageDimensions({
                                  width: canvas.width,
                                  height: canvas.height,
                              });
                              return; // Exit early since we've updated everything
                          }
                      }
                  }
                  
                  // Default: use calculated scale
                  canvas.width = scaledViewport.width;
                  canvas.height = scaledViewport.height;
                  canvas.style.width = `${scaledViewport.width}px`;
                  canvas.style.height = `${scaledViewport.height}px`;

                  setPageDimensions({
                      width: canvas.width,
                      height: canvas.height,
                  });
          });
      }
  };
  
  useEffect(() => {
    if (renderedPages === numPages && numPages !== null) {
      // Clear orientation-change guard after all pages re-render and layout stabilizes.
      // 500ms buffer ensures React has flushed any pending scroll/pageNumber updates
      // from the re-layout before the optin modal logic is allowed to run again.
      if (isOrientationChangingRef.current) {
        setTimeout(() => {
          isOrientationChangingRef.current = false;
          window.__kdf_orientation_changing = false;
        }, 500);
      }
      if (onLoadSuccess) {
        onLoadSuccess();
      }
    }
  }, [renderedPages, numPages]);

  useEffect(() => {
    const wrapper = containerRef.current;
    if (wrapper && numPages > 1) {
      wrapper.addEventListener("scroll", onScroll);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener("scroll", onScroll);
      }
    };
  }, [numPages]);

  useEffect(() => {
    searchInPdf();
  }, []);

  useEffect(() => {
    if (jumpPageNum !== null) {
      // Mark as programmatic scroll for jump-to-page functionality.
      // Do NOT clear jumpPageNum here — inputRef reads it to trigger scrollIntoView.
      // Clear it after a short delay so inputRef has time to fire on the next render.
      isProgrammaticScrollRef.current = true;
      setTimeout(() => setJumpPageNum(null), 200);
    }
  }, [jumpPageNum]);

  // Reset loading state when PDF file changes
  useEffect(() => {
    setNumPages(null);
    setRenderedPages(0);
  }, [pdfFile]);

  // Mobile landscape CYOA: Show only current page (production approach)
  // CSS hides all pages with visibility:hidden, then shows only .current-page
  // This naturally limits scrolling to the visible page without JavaScript
  // No scroll limiting logic needed - CSS handles it automatically


  const renderPage = (index) => (
    <>
      {editortype === "editor" && (
        <TagEditorSection
          docFileId={docFileId}
          numPages={numPages}
          docId={docId}
          docFiles={docFiles}
          currentPage={index + 1}
        />
      )}
      <div style={{ cursor: editEnabled ? "crosshair" : "default" }}>
        <Page
          id={`pageContainer${index + 1}`}
          className={`page ${pageNum === index + 1 ? 'current-page' : ''}`}
          pageNumber={index + 1}
          scale={parseFloat(zoom?.value)}
          onRenderSuccess={onPageRenderSuccess}
          onLoadSuccess={(page) => onPageLoadSuccess(page, index)}
          inputRef={(ref) => {
            if (
              ref &&
              (pageNum === index + 1 || jumpPageNum === index + 1 || index + 1 === Number(page)) &&
              !editEnabled &&
              !editElement?.id
            ) {
              if (prevZoomRef.current !== zoom.value) {
                prevZoomRef.current = zoom.value;
              } else if (
                // Only scroll on INTENTIONAL page changes, not during normal scrolling
                isProgrammaticScrollRef.current ||
                jumpPageNum === index + 1 ||
                (isFirstRender && index + 1 === Number(page))
              ) {
                // CYOA: never use scrollIntoView — body is position:fixed in portrait
                // which causes iOS Safari to navigate away ("Can't reach page").
                // CYOA page visibility is handled by CSS (.current-page-container).
                const isCYOA = document.querySelector('.hidden-for-cyoa') !== null;
                if (!isCYOA) {
                  setTimeout(() => {
                    ref.scrollIntoView({ behavior: "instant" });
                    isProgrammaticScrollRef.current = false;
                  }, 50);
                } else {
                  isProgrammaticScrollRef.current = false;
                }
              }
            }
          }}
        />
        <PdfCanvas
          zoom={zoom}
          canvasRefs={canvasRefs}
          index={index}
          editortype={editortype}
        />
      </div>
                   
             
      <PdfElementsList
        timerOnDoc={timerOnDoc}
        zoom={zoom}
        elemClick={elemClick}
        setVideoData={setVideoData}
        actionCableVideoMontior={actionCableVideoMontior}
        sendScrollInfo={sendScrollInfo}
        trackPageChange={trackPageChange}
        docFileId={docFileId}
        index={index}
        canvasRefs={canvasRefs}
        editortype={editortype}
        hoveredId={hoveredId}
        setJumpPageNum={setJumpPageNum}
        // scrollActive
      />
    </>
  );

  return (
    <div
      className="parent_doc_wrapper"
      id="parent_doc_wrapper"
      style={{
        ...(editortype !== "stats-viewer" && {
          '--page-container-height': `calc(100dvh - ${headerHeight}px)`,
          height: `var(--page-container-height)`,
          // CYOA: let CSS control overflow and scrollbar entirely.
          // Any inline overflow/scrollbar-width would override CSS !important and break landscape scroll.
          ...(!(editortype !== "editor" && tlb?.cyoa_enabled === true) && {
            overflowY: "scroll",
            scrollbarWidth: "none",
          }),
        }),
        position: "relative",
      }}
      ref={containerRef}
    >
      {/* Full-screen loader overlay */}
      {isPdfLoading && (
        <div className="pdf-loader-overlay">
          <div className="pdf-loader-content">
            <div className="pdf-loader-spinner"></div>
            {/* Loading KDF text is commented out - only showing spinner */}
            {/* <p className="pdf-loader-text">Loading KDF...</p> */}
          </div>
        </div>
      )}
      <Document
        file={pdfFile}
        onLoadSuccess={onDocumentLoadSuccess}
        className={`react-pdf__Document ${isPdfLoading ? 'pdf-loading-hidden' : ''}`}
        loading=""
      >
        {editortype == "stats-viewer" ? (
          <div className="pdf-page" style={{ position: "relative" }}>{renderPage(pageNum - 1)}</div>
        ) : (
          <>
            {Array.from(new Array(numPages), (el, index) => (
              <div
                className={`pdf-page ${pageNum === index + 1 ? 'current-page-container' : ''}`}
                key={`${index}-${orientationKey}`}
                style={{ position: "relative", marginBottom: "6px" }}
              >
                {renderPage(index)}
              </div>
            ))}
          </>
        )}
      </Document>
      {editortype !== "stats-viewer" && <Footer isEditor={true} />}
      {editortype == "editor" && (
        <ElementOrder docFileId={docFileId} setHoveredId={setHoveredId} />
      )}
      <div className="landscape-scroll-spacer" aria-hidden="true" />
    </div>
  );
};

export default PdfViewer;
